import { SelectProps, TextFieldProps } from '@shopify/polaris'

import { MemoedSelect, MemoedTextField } from './helpers'
import { HelpLabel } from './HelpIcon'
import { SellingPlanPricingPolicyAdjustmentType } from './plan-models'

const priceAdjustmentTypeOptions = [
  {
    label: 'Fixed amount discount',
    value: SellingPlanPricingPolicyAdjustmentType.FIXED_AMOUNT,
  },
  { label: 'Percentage discount', value: SellingPlanPricingPolicyAdjustmentType.PERCENTAGE },
  { label: 'Manual price', value: SellingPlanPricingPolicyAdjustmentType.PRICE },
]

interface PriceAdjustmentTypeFieldProps
  extends Omit<SelectProps, 'label' | 'onChange' | 'options' | 'value'> {
  value: SellingPlanPricingPolicyAdjustmentType
  onChange: (val: SellingPlanPricingPolicyAdjustmentType, id: string) => void
}

export const PriceAdjustmentTypeField = ({ ...props }: PriceAdjustmentTypeFieldProps) => (
  <MemoedSelect
    label={
      <HelpLabel tooltip="Discount or customer price applied initially to the base product price">
        Payment discount or custom price
      </HelpLabel>
    }
    options={[
      {
        label: 'None',
        value: SellingPlanPricingPolicyAdjustmentType.NONE,
      },
      ...priceAdjustmentTypeOptions,
    ]}
    {...props}
  />
)

export const SecondaryPriceAdjustmentTypeField = ({
  freeTrial,
  ...props
}: { freeTrial?: boolean } & PriceAdjustmentTypeFieldProps) => (
  <MemoedSelect
    label={freeTrial ? 'Post trial adjustment type' : 'Adjustment type'}
    options={
      freeTrial
        ? [
            {
              label: 'None',
              value: SellingPlanPricingPolicyAdjustmentType.NONE,
            },
            ...priceAdjustmentTypeOptions,
          ]
        : priceAdjustmentTypeOptions
    }
    {...props}
  />
)

type PriceAdjustmentValueFieldProps = Omit<
  TextFieldProps,
  'label' | 'type' | 'autoComplete' | 'prefix' | 'suffix'
> & {
  prePaidDeliveries: number
  adjustmentType: SellingPlanPricingPolicyAdjustmentType
  currencySymbol: string
  onChange: (value: string, id: string) => void
  label?: React.ReactNode
}
export const getPriceAdjustmentValueError = (
  type: SellingPlanPricingPolicyAdjustmentType,
  value?: string
): string | undefined => {
  if (!value || isNaN(parseFloat(value))) {
    return undefined
  }

  if (parseFloat(value) < 0) {
    return 'Adjustment value must be greater than or equal to 0'
  }

  if (type === SellingPlanPricingPolicyAdjustmentType.PERCENTAGE && parseFloat(value) > 100) {
    return 'Adjustment value must be less than or equal 100'
  }

  return undefined
}

const PriceAdjustmentValueLabel = ({
  adjustmentType,
  prePaidDeliveries,
}: {
  adjustmentType: SellingPlanPricingPolicyAdjustmentType
  prePaidDeliveries: number
}) => {
  const prePaidNote =
    prePaidDeliveries > 1
      ? '. Note: Shopify may adjust the final price to be evenly divisible by the amount of pre-paid deliveries'
      : ''

  if (adjustmentType !== SellingPlanPricingPolicyAdjustmentType.PRICE) {
    return (
      <HelpLabel
        tooltip={`Discount or price adjustment applied to the base product price${prePaidNote}`}
      >
        Adjustment value
      </HelpLabel>
    )
  }

  return <>Price</>
}

export const PriceAdjustmentValueField = ({
  prePaidDeliveries,
  adjustmentType,
  currencySymbol,
  value,
  disabled,
  ...props
}: PriceAdjustmentValueFieldProps) => (
  <MemoedTextField
    label={
      <PriceAdjustmentValueLabel
        adjustmentType={adjustmentType}
        prePaidDeliveries={prePaidDeliveries}
      />
    }
    type="number"
    autoComplete="off"
    prefix={
      adjustmentType !== SellingPlanPricingPolicyAdjustmentType.PERCENTAGE
        ? currencySymbol
        : undefined
    }
    suffix={adjustmentType === SellingPlanPricingPolicyAdjustmentType.PERCENTAGE ? '%' : undefined}
    disabled={disabled || adjustmentType === SellingPlanPricingPolicyAdjustmentType.NONE}
    value={adjustmentType === SellingPlanPricingPolicyAdjustmentType.NONE ? '0' : value}
    min="0"
    helpText={
      adjustmentType === SellingPlanPricingPolicyAdjustmentType.PERCENTAGE
        ? '0% = no discount; 100% = full discount'
        : undefined
    }
    {...props}
  />
)
